'use client';

import { faCheckCircle, faCopy } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Editor from '@monaco-editor/react';
import TextField from '@mui/material/TextField';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Button, Modal, Tabs, Typography } from '@scale/scaleui';
import { JsonataVariable } from '@scale/shared/chat/response';
import { useCallback, useEffect, useState } from 'react';
import { ReactJsonView } from 'react-scale/corp/components/ReactJsonView';

type JsonMode = 'Read-only' | 'Editable';

const TABS: { id: JsonMode }[] = [{ id: 'Read-only' }, { id: 'Editable' }];

function convertToJSONata(pathList: (string | number)[]): string {
  const isFromRoot = pathList[0] === 'root';
  const startIndex = isFromRoot ? 1 : 0;

  return pathList
    .slice(startIndex)
    .map((part, index) => {
      if (typeof part === 'number') {
        return `[${part}]`;
      } else if (!isNaN(Number(part))) {
        return `[${part}]`;
      } else if (/[^a-zA-Z0-9_]/.test(part.toString())) {
        return `["${part}"]`;
      } else {
        return index === 0 && startIndex === 0 ? part : `.${part}`;
      }
    })
    .join('')
    .replace(/^\./, ''); // Remove leading dot for the first key
}

interface SampleLintInputColumnProps {
  context: any;
  variables: JsonataVariable[];
  onContextChange: (newContext: any) => void;
}

function SampleLintInputColumn({
  context,
  variables,
  onContextChange,
}: SampleLintInputColumnProps) {
  const [jsonMode, setJsonMode] = useState<JsonMode>('Read-only');
  const [localContext, setLocalContext] = useState<string>('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [filteredRows, setFilteredRows] = useState(variables ?? []);
  const [copyMessage, setCopyMessage] = useState<string | null>(null);

  useEffect(() => {
    if (context) {
      const contextString = JSON.stringify(context, null, 2);
      setLocalContext(contextString);
    }
  }, [context]);

  useEffect(() => {
    setFilteredRows(
      variables.filter(
        variable =>
          variable.label.toLowerCase().includes(searchText.toLowerCase()) ||
          variable.value.toString().toLowerCase().includes(searchText.toLowerCase()),
      ),
    );
  }, [searchText, variables]);

  const handleChangeJsonMode = useCallback(
    (newMode: JsonMode) => {
      if (newMode !== jsonMode) {
        setJsonMode(newMode);
      }
    },
    [jsonMode],
  );

  const handleEditorChange = (text: string | undefined) => {
    setLocalContext(text ?? '');
    try {
      const updatedContext = JSON.parse(text ?? '{}');
      onContextChange(updatedContext);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Invalid JSON input:', e);
    }
  };

  const handleCopy = (expression: string) => {
    navigator.clipboard.writeText(expression);
    setCopyMessage('Copied!');
    setTimeout(() => setCopyMessage(null), 1500); // Hide the message after 1.5 seconds
  };

  const tableData = filteredRows.map((variable, index) => ({
    id: index,
    label: variable.label,
    value: variable.value,
    copy: (
      <Button
        size="sm"
        variant="outline"
        onClick={() => handleCopy(variable.expression)}
        style={{ fontSize: '0.65rem' }}
      >
        <FontAwesomeIcon icon={faCopy} style={{ fontSize: '0.65rem' }} />
      </Button>
    ),
  }));

  const columns: GridColDef[] = [
    {
      field: 'copy',
      headerName: 'Copy Path',
      flex: 1,
      minWidth: 100,
      sortable: false,
      renderCell: params => params.value,
    },
    { field: 'label', headerName: 'Label', flex: 1, minWidth: 500 },
    { field: 'value', headerName: 'Value', flex: 1, minWidth: 250 },
  ];

  return (
    <div className="border border-neutral-300 p-3 rounded-md flex flex-col h-full">
      <div className="flex justify-between items-center">
        <Typography.Subtitle2>Context</Typography.Subtitle2>
        <Tabs
          onChange={handleChangeJsonMode as (val: string) => void}
          tabs={TABS}
          value={jsonMode}
          variant="default"
        />
        <Button onClick={() => setIsModalOpen(true)}>View Available Variables</Button>
      </div>

      {localContext ? (
        jsonMode === 'Read-only' ? (
          <div className="overflow-y-auto h-[565px] pb-[200px]">
            <ReactJsonView
              src={JSON.parse(localContext)}
              theme="rjv-default"
              collapsed={1}
              enableClipboard={({ _src, namespace }) => {
                const jsonataExpression = convertToJSONata(namespace);
                navigator.clipboard.writeText(jsonataExpression);
              }}
              name={'root'}
              displayDataTypes={false}
              displayObjectSize={false}
            />
          </div>
        ) : (
          <div className="h-[620px] overflow-x-scroll mt-2">
            <Editor
              language="json"
              options={{
                automaticLayout: true,
                autoClosingBrackets: 'always',
                autoClosingQuotes: 'always',
                formatOnPaste: true,
                formatOnType: true,
                scrollBeyondLastLine: true,
                tabSize: 2,
                minimap: { enabled: false },
                lineNumbers: 'on',
                wordWrap: 'off',
              }}
              value={localContext}
              onChange={handleEditorChange}
            />
          </div>
        )
      ) : (
        <Typography.Paragraph1>No context available to display</Typography.Paragraph1>
      )}

      <Modal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Available Variables"
        className="scaleui scale-theme scale-tailwind"
      >
        <div className="p-4" style={{ height: 455, width: 900 }}>
          <TextField
            label="Search"
            variant="outlined"
            size="small"
            fullWidth
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            style={{ marginBottom: '10px', marginTop: '-10px' }}
          />
          <DataGrid rows={tableData} columns={columns} pageSize={5} disableColumnMenu autoHeight />
          {copyMessage && (
            <div
              style={{
                marginTop: '10px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                color: '#4CAF50',
              }}
            >
              <FontAwesomeIcon icon={faCheckCircle} />
              {copyMessage}
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
}

export default SampleLintInputColumn;
